/**
 * The superadmin console API (§7, "Superadmin"). Every call the console makes
 * that is *not* an island endpoint lives here; island membership and
 * invitations deliberately go through the ordinary room endpoints, which the
 * policy module already opens to superadmins.
 *
 * Failures arrive as `ApiRequestError` — the same one the invitation flow uses
 * — because the console branches on the backend's machine-readable `code`:
 * `last_superadmin`, `self_action`, `user_active`, `organization_has_islands`
 * and the rest all become specific copy rather than a bare status.
 */

import config from '@/env-config';
import { ApiRequestError } from '@/services/invitation-api';
import { formatApiErrorMessage } from '@/utils/api-error';
import type {
  AdminIslandDetail,
  AdminIslandRow,
  AdminListResult,
  AdminUserDetail,
  AdminUserListResult,
  AdminUserRow,
  InvitationSummary,
  OrganizationSummary,
  PlatformRole,
  PresenceSnapshot
} from 'minka-shared';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('jwt_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

async function request<T>(
  path: string,
  init: RequestInit,
  fallback: string
): Promise<T> {
  const response = await fetch(`${config.api.baseUrl}/admin${path}`, {
    ...init,
    headers: authHeaders()
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      formatApiErrorMessage(body, `${fallback}: ${response.status}`),
      response.status,
      typeof body?.code === 'string' ? body.code : undefined
    );
  }

  return body.data as T;
}

/** `?q=` only: the console has no pagination (§7). */
function searchQuery(q?: string): string {
  const term = q?.trim();
  return term ? `?q=${encodeURIComponent(term)}` : '';
}

// Users

export function listUsers(q?: string): Promise<AdminUserListResult> {
  return request(`/users${searchQuery(q)}`, { method: 'GET' }, 'Failed to load users');
}

export function getUserDetail(userId: string): Promise<AdminUserDetail> {
  return request(`/users/${userId}`, { method: 'GET' }, 'Failed to load the user');
}

export function deactivateUser(userId: string): Promise<AdminUserRow> {
  return request(`/users/${userId}/deactivate`, { method: 'POST' }, 'Failed to deactivate the account');
}

export function reactivateUser(userId: string): Promise<AdminUserRow> {
  return request(`/users/${userId}/reactivate`, { method: 'POST' }, 'Failed to reactivate the account');
}

export function setPlatformRole(userId: string, platformRole: PlatformRole): Promise<AdminUserRow> {
  return request(
    `/users/${userId}/platform-role`,
    { method: 'PUT', body: JSON.stringify({ platformRole }) },
    'Failed to change the platform role'
  );
}

/** Tombstone a deactivated account's address; the server picks the address. */
export function changeUserEmail(userId: string): Promise<AdminUserRow> {
  return request(
    `/users/${userId}/email`,
    { method: 'PUT', body: JSON.stringify({}) },
    'Failed to change the email address'
  );
}

// Platform invitations

export function createPlatformInvitation(email: string): Promise<InvitationSummary> {
  return request(
    '/invitations',
    { method: 'POST', body: JSON.stringify({ email }) },
    'Failed to send the invitation'
  );
}

export async function listPlatformInvitations(): Promise<InvitationSummary[]> {
  const data = await request<{ invitations: InvitationSummary[] }>(
    '/invitations?scope=platform',
    { method: 'GET' },
    'Failed to load invitations'
  );
  return data?.invitations ?? [];
}

export function revokePlatformInvitation(invitationId: string): Promise<{ id: string }> {
  return request(`/invitations/${invitationId}`, { method: 'DELETE' }, 'Failed to revoke the invitation');
}

export function resendPlatformInvitation(invitationId: string): Promise<InvitationSummary> {
  return request(
    `/invitations/${invitationId}/resend`,
    { method: 'POST' },
    'Failed to resend the invitation'
  );
}

// Islands

export function listIslands(q?: string): Promise<AdminListResult<AdminIslandRow>> {
  return request(`/islands${searchQuery(q)}`, { method: 'GET' }, 'Failed to load islands');
}

export function getIslandDetail(roomId: string): Promise<AdminIslandDetail> {
  return request(`/islands/${roomId}`, { method: 'GET' }, 'Failed to load the island');
}

export function setIslandOrganization(
  roomId: string,
  organizationId: string
): Promise<AdminIslandRow> {
  return request(
    `/islands/${roomId}/organization`,
    { method: 'PUT', body: JSON.stringify({ organizationId }) },
    'Failed to move the island'
  );
}

export function deleteIsland(roomId: string): Promise<{ id: string }> {
  return request(`/islands/${roomId}`, { method: 'DELETE' }, 'Failed to delete the island');
}

// Organizations

export function listOrganizations(q?: string): Promise<AdminListResult<OrganizationSummary>> {
  return request(
    `/organizations${searchQuery(q)}`,
    { method: 'GET' },
    'Failed to load organizations'
  );
}

export function renameOrganization(
  organizationId: string,
  name: string
): Promise<OrganizationSummary> {
  return request(
    `/organizations/${organizationId}`,
    { method: 'PATCH', body: JSON.stringify({ name }) },
    'Failed to rename the organization'
  );
}

export function deleteOrganization(organizationId: string): Promise<{ id: string }> {
  return request(
    `/organizations/${organizationId}`,
    { method: 'DELETE' },
    'Failed to delete the organization'
  );
}

// Presence

/** One snapshot of who is online and where (§6.7). Never polled. */
export function getPresence(): Promise<PresenceSnapshot> {
  return request('/presence', { method: 'GET' }, 'Failed to load presence');
}
